import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

type Language = 'pashto' | 'dari';
type RecorderState = 'idle' | 'recording' | 'ready' | 'running' | 'done' | 'failed';

interface SpeechLog {
  id: number;
  operation: 'STT' | 'TTS';
  language: Language;
  status: string;
  endpoint?: string;
  inputText?: string;
  outputText?: string;
  sourceFileName?: string;
  sourceContentType?: string;
  sourceSizeBytes?: number;
  outputContentType?: string;
  outputSizeBytes?: number;
  languageProbability?: number;
  audioDurationSeconds?: number;
  latencyMs?: number;
  requestPayload?: string;
  responsePayload?: string;
  errorMessage?: string;
  createdAt?: string;
  inputAudioUrl?: string;
  outputAudioUrl?: string;
  expanded?: boolean;
}

interface ServiceStatus {
  mode?: string;
  configured?: boolean;
  baseUrl?: string;
  apiKeyConfigured?: boolean;
  serviceReachable?: boolean;
  health?: string;
}

@Component({
  selector: 'app-pashto-dari-speech',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pashto-dari-speech.component.html',
  styleUrls: ['./pashto-dari-speech.component.scss'],
})
export class PashtoDariSpeechComponent implements OnInit, OnDestroy {
  language: Language = 'pashto';
  state: RecorderState = 'idle';
  elapsedSeconds = 0;
  selectedFile: File | null = null;
  sourceAudioUrl = '';
  transcription = '';
  sttResult: SpeechLog | null = null;
  ttsText = 'سلام، زه په پښتو خبرې کوم.';
  noiseScale = 0.4;
  noiseScaleW = 0.8;
  lengthScale = 1;
  ttsRunning = false;
  ttsResult: SpeechLog | null = null;
  generatedAudioUrl = '';
  errorMessage = '';
  status: ServiceStatus | null = null;
  statusLoading = false;
  logs: SpeechLog[] = [];
  logsLoading = false;
  logFilter: 'ALL' | 'STT' | 'TTS' = 'ALL';

  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private recordedBlob: Blob | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;
  private mimeType = '';

  constructor(private readonly http: HttpClient) {}

  ngOnInit(): void {
    void Promise.all([this.loadStatus(), this.loadLogs()]);
  }

  get filteredLogs(): SpeechLog[] {
    return this.logFilter === 'ALL' ? this.logs : this.logs.filter(log => log.operation === this.logFilter);
  }

  get canTranscribe(): boolean {
    return (!!this.selectedFile || !!this.recordedBlob) && this.state !== 'recording' && this.state !== 'running';
  }

  get speechHealth(): any {
    if (!this.status?.health) return null;
    try { return JSON.parse(this.status.health); } catch { return null; }
  }

  get sttModelLabel(): string {
    return this.speechHealth?.pashto?.stt?.model || 'Pashto Whisper';
  }

  get ttsModelLabel(): string {
    return this.speechHealth?.pashto?.tts?.model || 'Pashto VITS2';
  }

  get dariAvailable(): boolean {
    return !!(this.speechHealth?.dari?.stt?.available && this.speechHealth?.dari?.tts?.available);
  }

  get durationLabel(): string {
    return `${Math.floor(this.elapsedSeconds / 60).toString().padStart(2, '0')}:${(this.elapsedSeconds % 60).toString().padStart(2, '0')}`;
  }

  setLanguage(language: Language): void {
    this.language = language;
    if (!this.ttsText || this.ttsText === 'سلام، زه په پښتو خبرې کوم.' || this.ttsText === 'سلام، من به زبان دری صحبت می‌کنم.') {
      this.ttsText = language === 'pashto' ? 'سلام، زه په پښتو خبرې کوم.' : 'سلام، من به زبان دری صحبت می‌کنم.';
    }
    this.noiseScale = language === 'pashto' ? 0.4 : 0.667;
  }

  async loadStatus(): Promise<void> {
    this.statusLoading = true;
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiBase}/status`));
      this.status = response?.data || response;
    } catch {
      this.status = { configured: false, mode: 'unavailable' };
    } finally {
      this.statusLoading = false;
    }
  }

  async startRecording(): Promise<void> {
    this.clearSttAudio();
    this.errorMessage = '';
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
      this.mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t)) || '';
      this.recorder = new MediaRecorder(this.stream, this.mimeType ? { mimeType: this.mimeType } : undefined);
      this.chunks = [];
      this.startedAt = Date.now();
      this.elapsedSeconds = 0;
      this.recorder.ondataavailable = event => { if (event.data.size) this.chunks.push(event.data); };
      this.recorder.onstop = () => this.finishRecording();
      this.recorder.start(1000);
      this.state = 'recording';
      this.timer = setInterval(() => this.elapsedSeconds = Math.floor((Date.now() - this.startedAt) / 1000), 250);
    } catch (error) {
      this.fail(error, 'Microphone access failed.');
      this.stopMedia();
    }
  }

  stopRecording(): void {
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
  }

  chooseFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) return;
    this.clearSttAudio();
    if (file.size > 10 * 1024 * 1024) {
      this.errorMessage = 'Audio file must not exceed 10 MB.';
      input.value = '';
      return;
    }
    this.selectedFile = file;
    this.sourceAudioUrl = URL.createObjectURL(file);
    this.state = 'ready';
  }

  async runStt(): Promise<void> {
    const audio = this.selectedFile || this.recordedBlob;
    if (!audio) return;
    this.state = 'running';
    this.errorMessage = '';
    this.transcription = '';
    try {
      const form = new FormData();
      form.append('audio', audio, this.selectedFile?.name || `recording.${this.mimeType.includes('ogg') ? 'ogg' : 'webm'}`);
      form.append('language', this.language);
      const response = await firstValueFrom(this.http.post<any>(`${this.apiBase}/stt`, form));
      this.sttResult = response?.data || response;
      this.transcription = this.sttResult?.outputText || '';
      if (this.sttResult?.status === 'FAILED') throw new Error(this.sttResult.errorMessage || 'STT failed.');
      this.state = 'done';
      await this.loadLogs();
    } catch (error) {
      this.fail(error, 'STT request failed.');
      await this.loadLogs();
    }
  }

  async runTts(): Promise<void> {
    if (!this.ttsText.trim()) return;
    this.ttsRunning = true;
    this.errorMessage = '';
    this.revokeGeneratedAudio();
    try {
      const body = new HttpParams()
        .set('language', this.language).set('text', this.ttsText.trim())
        .set('noiseScale', String(this.noiseScale)).set('noiseScaleW', String(this.noiseScaleW))
        .set('lengthScale', String(this.lengthScale));
      const response = await firstValueFrom(this.http.post<any>(`${this.apiBase}/tts`, body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }));
      this.ttsResult = response?.data || response;
      if (this.ttsResult?.status === 'FAILED') throw new Error(this.ttsResult.errorMessage || 'TTS failed.');
      if (this.ttsResult?.id) this.generatedAudioUrl = await this.fetchAudio(this.ttsResult.id, 'output');
      await this.loadLogs();
    } catch (error) {
      this.fail(error, 'TTS request failed.');
      await this.loadLogs();
    } finally {
      this.ttsRunning = false;
    }
  }

  async loadLogs(): Promise<void> {
    this.logsLoading = true;
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiBase}/logs?limit=100`));
      this.logs.forEach(log => this.revokeLogUrls(log));
      this.logs = response?.data?.records || response?.records || [];
    } catch (error) {
      this.fail(error, 'Could not load test logs.');
    } finally {
      this.logsLoading = false;
    }
  }

  async toggleAudio(log: SpeechLog, kind: 'input' | 'output'): Promise<void> {
    const key = kind === 'input' ? 'inputAudioUrl' : 'outputAudioUrl';
    if (log[key]) {
      URL.revokeObjectURL(log[key]!);
      log[key] = '';
      return;
    }
    try { log[key] = await this.fetchAudio(log.id, kind); }
    catch (error) { this.fail(error, 'Could not load saved audio.'); }
  }

  async deleteLog(log: SpeechLog): Promise<void> {
    try {
      await firstValueFrom(this.http.delete(`${this.apiBase}/logs/${log.id}`));
      this.revokeLogUrls(log);
      this.logs = this.logs.filter(item => item.id !== log.id);
    } catch (error) { this.fail(error, 'Could not delete the log.'); }
  }

  bytes(value?: number): string {
    if (!value) return '—';
    return value < 1024 * 1024 ? `${Math.round(value / 1024)} KB` : `${(value / 1024 / 1024).toFixed(2)} MB`;
  }

  date(value?: string): string { return value ? new Date(value).toLocaleString() : '—'; }
  percent(value?: number): string { return value == null ? '—' : `${Math.round(value * 100)}%`; }
  pretty(value?: string): string {
    if (!value) return '';
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
  }

  ngOnDestroy(): void {
    this.stopMedia();
    this.clearSttAudio();
    this.revokeGeneratedAudio();
    this.logs.forEach(log => this.revokeLogUrls(log));
  }

  private finishRecording(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.stopMedia();
    this.recordedBlob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
    this.sourceAudioUrl = URL.createObjectURL(this.recordedBlob);
    this.state = this.recordedBlob.size ? 'ready' : 'failed';
  }

  private async fetchAudio(id: number, kind: 'input' | 'output'): Promise<string> {
    const blob = await firstValueFrom(this.http.get(`${this.apiBase}/logs/${id}/${kind}-audio`, { responseType: 'blob' }));
    return URL.createObjectURL(blob);
  }

  private clearSttAudio(): void {
    if (this.sourceAudioUrl) URL.revokeObjectURL(this.sourceAudioUrl);
    this.sourceAudioUrl = '';
    this.selectedFile = null;
    this.recordedBlob = null;
    this.transcription = '';
    this.sttResult = null;
    this.elapsedSeconds = 0;
    this.chunks = [];
  }

  private stopMedia(): void {
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;
  }

  private revokeGeneratedAudio(): void {
    if (this.generatedAudioUrl) URL.revokeObjectURL(this.generatedAudioUrl);
    this.generatedAudioUrl = '';
  }

  private revokeLogUrls(log: SpeechLog): void {
    if (log.inputAudioUrl) URL.revokeObjectURL(log.inputAudioUrl);
    if (log.outputAudioUrl) URL.revokeObjectURL(log.outputAudioUrl);
  }

  private fail(error: unknown, fallback: string): void {
    this.state = 'failed';
    this.errorMessage = error instanceof Error ? error.message : fallback;
  }

  private get apiBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/pashto-dari-speech`;
  }
}
